// Read-only view of the policy rule engine (Phase 2).
//
// Shows each branch its OWN policy, reflected back from the rules the Phase 1
// backfill wrote. The point of shipping read-only first is validation: if what
// people see here doesn't match what they believe their policy is, the backfill
// is wrong and we find out before anyone can edit anything.
//
// Editing arrives in Phase 3 — `editable` comes from the server so this page
// never has to guess.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  SlidersHorizontal, ChevronDown, ChevronRight, Info, Search,
  CircleSlash, Settings2, Globe, Users, CalendarClock, AlertTriangle,
  Pencil, Trash2, Eye, EyeOff,
} from 'lucide-react';
import {
  choiceLabel, describeAudience, formatDate, formatWindow, SCOPE_LABEL,
} from './policyLanguage';
import { buildRowGroups } from './policyRowGroups';
import { editableBaseRow, pauseState } from './policyEditTarget';
import { loadPredicateOptions } from './policyPredicateOptions';
import {
  createPolicyRule,
  deletePolicyRule,
  getEffectivePolicy,
  getPolicyCatalog,
  getPolicyCoverage,
  paramApplies,
  updatePolicyRule,
  type EffectivePolicy,
  type EffectiveRule,
  type PolicyCoverage,
  type PolicyParamSpec,
  type PolicyRuleRow,
  type PolicyRuleSpec,
  type PolicySource,
} from '../../api/policyRules';
import PolicyRuleEditor, { type EditorSubmit } from './PolicyRuleEditor';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import ConfirmDialog, { type ConfirmState } from '../../components/ConfirmDialog';

const SOURCE_META: Record<PolicySource, { label: string; hint: string; cls: string }> = {
  branch: {
    label: 'Configured',
    hint: 'A rule saved for this branch decides this.',
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  global: {
    label: 'Inherited',
    hint: 'No branch rule — a shared default row decides this.',
    cls: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  catalog_default: {
    label: 'System default',
    hint: 'Never configured. The engine falls back to its built-in behaviour.',
    cls: 'bg-gray-100 text-gray-600 border-gray-200',
  },
  off: {
    label: 'Not in use',
    hint: 'No rule exists, so this feature is switched off.',
    cls: 'bg-gray-50 text-gray-400 border-gray-200',
  },
};

// Deleting a rule that has already been in force does NOT remove it: the server
// CLOSES it (`effective_to` = yesterday) so recalculating an earlier month still
// resolves the policy that applied then. The row therefore survives the delete,
// and without this distinction it looked identical to a live one — which reads
// as "the delete silently failed".
function isRowInForce(
  row: { effective_from: string | null; effective_to: string | null },
  today: string,
): boolean {
  if (row.effective_from && row.effective_from > today) return false;
  if (row.effective_to && row.effective_to < today) return false;
  return true;
}

// How many rule rows are shown before the "Show all" link takes over.
const ROW_PREVIEW = 5;

// ISO date in LOCAL time. `toISOString()` would shift +0530 backwards and call
// a rule closed yesterday "still live" for the first 5.5 hours of the day.
function todayISO(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatValue(value: unknown, param?: PolicyParamSpec): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  // Engine tokens ("clock_time", "first_half") read as words, the same words
  // the editor's picker shows for them.
  if (param?.type === 'enum' && typeof value === 'string') return choiceLabel(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    if (typeof value[0] === 'object') return `${value.length} entr${value.length === 1 ? 'y' : 'ies'}`;
    if (param?.choices?.length) return value.map((v) => choiceLabel(String(v))).join(', ');
    return value.join(', ');
  }
  if (typeof value === 'object') {
    // A time_range param ({from, to}) reads as a span; a blank edge shows as
    // an open end rather than "null".
    const span = value as { from?: unknown; to?: unknown };
    if ('from' in span || 'to' in span) {
      return `${span.from || '…'} – ${span.to || '…'}`;
    }
    return JSON.stringify(value);
  }
  return String(value);
}

function humanize(name: string): string {
  const withSpaces = name.replace(/_/g, ' ');
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
}

function SourceBadge({ source }: { source: PolicySource }) {
  const meta = SOURCE_META[source] ?? SOURCE_META.off;
  return (
    <span
      title={meta.hint}
      className={`shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full border ${meta.cls}`}
    >
      {meta.label}
    </span>
  );
}

/** Employee id → display name, loaded lazily and cached for the session. */
function useEmployeeNames(enabled: boolean): Map<string, string> | null {
  const [names, setNames] = useState<Map<string, string> | null>(null);

  useEffect(() => {
    if (!enabled || names) return;
    let cancelled = false;
    loadPredicateOptions('employee_in')
      .then((options) => {
        if (cancelled || !options) return;
        setNames(new Map(options.map((o) => [o.value, o.label])));
      })
      // Names are a nicety — without them rows fall back to a count.
      .catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, [enabled, names]);

  return names;
}

function RuleCard({
  rule, spec, canEdit, onEdit, onDelete,
}: {
  rule: EffectiveRule;
  /** Catalog entry for this rule; undefined while the catalog is loading. */
  spec?: PolicyRuleSpec;
  canEdit: boolean;
  onEdit: (row: PolicyRuleRow | null) => void;
  onDelete: (row: PolicyRuleRow) => void;
}) {
  const [open, setOpen] = useState(false);
  const [showAllRows, setShowAllRows] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const params = rule.effective_params;
  // Values the engine is not reading in the current mode (a percent while
  // measuring by hours) are left out, matching what the editor shows.
  const hidden = new Set(
    (spec?.params ?? []).filter((p) => !paramApplies(p, params)).map((p) => p.name),
  );
  const paramSpecs = new Map((spec?.params ?? []).map((p) => [p.name, p]));
  const entries = params
    ? Object.entries(params).filter(([name]) => !hidden.has(name))
    : [];

  // Rows scoped to named people are the ones that read as noise, so their
  // names are fetched — but only once the card is open and only when such a
  // row exists. The lookup is cached across cards.
  const namesEab = open && rule.rows.some((r) =>
    'employee_in' in (r.applies_to ?? {}) || 'employee_not_in' in (r.applies_to ?? {}));
  const employeeNames = useEmployeeNames(namesEab);
  // Edit targets the branch's BASE row in force today — unscoped, and whatever
  // dates a previous edit left on it. It used to require an UNDATED row, which
  // stopped being true after the first edit (the server closes the old row and
  // dates the successor), so the button turned into Configure and the next
  // Save created an undated row that rewrote history. See policyEditTarget.ts.
  const branchRow = editableBaseRow(rule.rows);
  // The one rule whose ON state is a switch-off. Said out loud on the card,
  // because "0 late days everywhere" and "late-coming is paused" look the same
  // on every other screen.
  const pause = rule.key === 'late.suspension'
    ? pauseState(rule.effective_params, rule.rows)
    : null;

  return (
    <div className="border border-[#e8e3d8] rounded-xl bg-white">
      {/* View and Edit sit on the title line rather than at the foot of the
          expanded card, so both actions are reachable without opening the rule
          first. Configuring is the dialog's job — this row only chooses
          between reading the rule and editing it. */}
      <div className="flex items-start gap-3 p-3">
        <button
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="flex-1 min-w-0 flex items-start gap-3 text-left rounded-lg hover:bg-[#faf8f3] transition-colors"
        >
          <span className="mt-0.5 text-gray-400 shrink-0">
            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </span>
          <span className="flex-1 min-w-0">
            <span className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-[15px] text-gray-900">{rule.title}</span>
              <SourceBadge source={rule.source} />
              {rule.has_predicated_rows && (
                <span
                  title="Some rules here apply only to certain people — pick an employee to see their answer."
                  className="inline-flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full"
                >
                  <Users size={11} /> Varies by employee
                </span>
              )}
              {rule.has_windowed_rows && (
                <span
                  title="Some rules here only apply between certain dates."
                  className="inline-flex items-center gap-1 text-[11px] text-violet-700 bg-violet-50 border border-violet-200 px-2 py-0.5 rounded-full"
                >
                  <CalendarClock size={11} /> Date-limited
                </span>
              )}
            </span>
            <span className="block text-xs text-gray-500 mt-0.5">{rule.question}</span>
          </span>
        </button>

        {/* Labels collapse to icons under `sm`: at 375px the two buttons
            otherwise take 162px of the row and squeeze the title into a
            three-line column. aria-label keeps the name either way. */}
        <span className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? 'Hide' : 'View'}
            className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 border border-[#e8e3d8] rounded-lg px-2 sm:px-2.5 py-1 hover:bg-[#faf8f3] transition-colors"
          >
            {open ? <EyeOff size={12} /> : <Eye size={12} />}
            <span className="hidden sm:inline">{open ? 'Hide' : 'View'}</span>
          </button>
          {canEdit && (
            <button
              onClick={() => onEdit(branchRow)}
              aria-label={branchRow ? 'Edit' : 'Configure'}
              className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 border border-emerald-200 bg-emerald-50 rounded-lg px-2 sm:px-2.5 py-1 hover:bg-emerald-100 transition-colors"
            >
              <Pencil size={12} />
              <span className="hidden sm:inline">{branchRow ? 'Edit' : 'Configure'}</span>
            </button>
          )}
        </span>
      </div>

      {pause?.active && (
        <div
          role="status"
          className="mx-3 mb-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-900"
        >
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <p>
            <strong>Late-coming is paused</strong>
            {pause.until ? ` until ${formatDate(pause.until)}` : ' with no end date'}
            . No day counts as late, nobody is warned and nothing is held while this is on.
            {!pause.until && ' Give it an end date, or switch it off.'}
          </p>
        </div>
      )}

      {open && (
        <div className="px-3 pb-3 pl-10 space-y-3">
          {!params ? (
            <p className="text-sm text-gray-500 flex items-center gap-2">
              <CircleSlash size={14} className="text-gray-400" />
              Not in use. No rule is configured, so the engine skips this entirely.
            </p>
          ) : (
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
              {entries.map(([name, value]) => (
                <div key={name} className="flex justify-between gap-3 text-sm border-b border-[#f4f1e8] py-1">
                  <dt className="text-gray-500">{humanize(name)}</dt>
                  <dd className="font-medium text-gray-900 text-right break-words">
                    {formatValue(value, paramSpecs.get(name))}
                  </dd>
                </div>
              ))}
            </dl>
          )}

          {rule.rows.length > 0 && (() => {
            const today = todayISO();
            const live = rule.rows.filter((r) => isRowInForce(r, today));
            const ended = rule.rows.filter((r) => !isRowInForce(r, today));
            const ordered = [...live, ...ended];
            // A migrated rule carries one row per employee — 20+ lines that all
            // read the same and bury the catch-all. Same-shaped rows collapse
            // into one expandable line; whatever is left is capped.
            const groups = buildRowGroups(ordered, (r) => isRowInForce(r, today));
            const visible = showAllRows ? groups : groups.slice(0, ROW_PREVIEW);

            const renderRow = (row: PolicyRuleRow) => {
              const inForce = isRowInForce(row, today);
              const audience = describeAudience(row.applies_to, employeeNames);
              const window = formatWindow(row.effective_from, row.effective_to);
              const everyone = Object.keys(row.applies_to).length === 0;
              return (
                <div
                  key={row.id}
                  // The technical detail moves into the tooltip rather than
                  // being printed on every row.
                  title={[
                    `${SCOPE_LABEL[row.scope] ?? row.scope} rule`,
                    row.priority > 0
                      ? `Priority ${row.priority} — when several rules match the same person, the higher number wins.`
                      : '',
                    everyone ? 'Applies to everyone who matches no exception above.' : '',
                  ].filter(Boolean).join('\n')}
                  className={`flex items-center gap-2 flex-wrap ${inForce ? '' : 'opacity-55'}`}
                >
                  <span className={`inline-flex items-center gap-1 ${everyone ? 'font-medium text-gray-600' : ''}`}>
                    {row.scope === 'global' ? <Globe size={11} /> : <Settings2 size={11} />}
                    {audience}
                  </span>
                  {window && (
                    <span className="inline-flex items-center gap-1 text-violet-700">
                      <CalendarClock size={11} /> {window}
                    </span>
                  )}
                  {row.auto_generated && (
                    <span
                      title="Created automatically from your existing payroll settings when this page was switched on."
                      className="text-gray-400"
                    >
                      · set up automatically
                    </span>
                  )}
                  {!inForce && (
                    <span
                      title={`Stopped applying after ${row.effective_to}. Kept so past payroll still resolves it.`}
                      className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-1.5 py-px text-[10px] font-medium text-gray-500"
                    >
                      no longer applies
                    </span>
                  )}
                  {/* Edit / delete only on a row that is still in force. An
                      ended row is history: editing it would rewrite what
                      already happened, and deleting it again does nothing. */}
                  {canEdit && row.scope === 'branch' && inForce && (
                    <span className="inline-flex items-center gap-1.5 ml-auto">
                      <button
                        onClick={() => onEdit(row)}
                        aria-label={`Edit rule for ${rule.title}`}
                        className="text-gray-400 hover:text-emerald-700"
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        onClick={() => onDelete(row)}
                        aria-label={`Remove rule for ${rule.title}`}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <Trash2 size={12} />
                      </button>
                    </span>
                  )}
                </div>
              );
            };

            return (
            <div className="text-xs text-gray-500 space-y-1">
              <div className="font-medium text-gray-600">
                {live.length > 0
                  ? `${live.length} active rule${live.length === 1 ? '' : 's'}`
                  : 'Nothing applies right now'}
                {ended.length > 0 && (
                  <span className="font-normal text-gray-400">
                    {' '}· {ended.length} no longer used
                  </span>
                )}
              </div>
              {ended.length > 0 && live.length === 0 && (
                // The exact confusion this caused: a deleted rule is still
                // listed, so say plainly that it no longer applies and why it
                // is still here.
                <p className="text-[11px] text-gray-400 leading-snug">
                  Removed rules are kept, not erased — recalculating an earlier
                  month has to see the policy that applied then. They no longer
                  affect anything from their end date onward.
                </p>
              )}
              {visible.map((group) => {
                if (group.rows.length === 1) return renderRow(group.rows[0]);

                // A run of identical-shaped rows: one line, opened on demand.
                const first = group.rows[0];
                const groupOpen = !!openGroups[group.key];
                const window = formatWindow(first.effective_from, first.effective_to);
                return (
                  <div key={group.key} className={isRowInForce(first, today) ? '' : 'opacity-55'}>
                    <button
                      onClick={() => setOpenGroups((g) => ({ ...g, [group.key]: !groupOpen }))}
                      aria-expanded={groupOpen}
                      className="flex items-center gap-1.5 flex-wrap text-left hover:text-gray-700"
                    >
                      {groupOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                      <span className="font-medium text-gray-600">{group.label}</span>
                      {window && (
                        <span className="inline-flex items-center gap-1 text-violet-700">
                          <CalendarClock size={11} /> {window}
                        </span>
                      )}
                      {first.auto_generated && (
                        <span className="text-gray-400">· set up automatically</span>
                      )}
                      {!isRowInForce(first, today) && (
                        <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-1.5 py-px text-[10px] font-medium text-gray-500">
                          no longer applies
                        </span>
                      )}
                    </button>
                    {groupOpen && (
                      <div className="pl-4 mt-1 space-y-1 border-l border-[#f0ece2]">
                        {group.rows.map(renderRow)}
                      </div>
                    )}
                  </div>
                );
              })}

              {groups.length > ROW_PREVIEW && (
                <button
                  onClick={() => setShowAllRows((s) => !s)}
                  className="text-[11px] font-medium text-emerald-700 hover:underline"
                >
                  {showAllRows
                    ? 'Show fewer'
                    : `Show all ${groups.length} — ${groups.length - ROW_PREVIEW} more`}
                </button>
              )}
            </div>
            );
          })()}

        </div>
      )}
    </div>
  );
}

export default function PolicyRules() {
  const { user } = useAuth();
  const [policy, setPolicy] = useState<EffectivePolicy | null>(null);
  const [coverage, setCoverage] = useState<PolicyCoverage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [specs, setSpecs] = useState<Record<string, PolicyRuleSpec>>({});
  const [predicates, setPredicates] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<{
    spec: PolicyRuleSpec;
    row: PolicyRuleRow | null;
    seed: Record<string, unknown> | null;
    exception: boolean;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // In-app confirmation dialog (replaces native window.confirm).
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  const canEdit = Boolean(policy?.editable && policy?.can_manage);

  const reload = useCallback(async () => {
    const data = await getEffectivePolicy();
    setPolicy(data);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // The catalog carries the param schemas the editor renders from, so it
        // loads alongside the effective policy rather than on first edit.
        const [data, catalog] = await Promise.all([
          getEffectivePolicy(),
          getPolicyCatalog(),
        ]);
        if (cancelled) return;
        setPolicy(data);
        const byKey: Record<string, PolicyRuleSpec> = {};
        catalog.sections.forEach((s) => s.rules.forEach((r) => { byKey[r.key] = r; }));
        setSpecs(byKey);
        setPredicates(catalog.predicates);
      } catch (err: any) {
        if (!cancelled) {
          setError(
            err?.response?.status === 403
              ? "You don't have access to policy rules."
              : err?.response?.data?.detail || 'Could not load policy rules.',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (payload: EditorSubmit) => {
    if (!editing) return;
    setSaving(true);
    setSaveError(null);
    try {
      if (editing.row) {
        const res = await updatePolicyRule(editing.row.id, payload);
        const closed = res.also_closed?.length ?? 0;
        toast.success(
          res.mode === 'superseded'
            ? `Saved. The previous version is kept for past dates.${
              closed ? ` ${closed} other open ${closed === 1 ? 'version' : 'versions'} of this rule closed too.` : ''}`
            : 'Saved.',
        );
      } else {
        const created = await createPolicyRule({ rule_key: editing.spec.key, ...payload });
        // The server dates a new row on a key with history from today, so a
        // blank start date never rewrites earlier months. Say so — the admin
        // left it blank and would otherwise assume "this whole month".
        toast.success(
          created.starts_today
            ? 'Rule created — applies from today. Earlier dates keep the previous version.'
            : 'Rule created.',
        );
      }
      setEditing(null);
      await reload();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || 'Could not save this rule.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (row: PolicyRuleRow) => {
    // Says up front that the row will still be listed. It is not obvious that
    // "delete" means "stop applying", and a rule that reappears in the list
    // looks like a failure rather than a deliberate audit trail.
    setConfirm({
      title: 'Stop applying this rule from today?',
      description:
        'It stays in the list marked "ended" rather than being erased — '
        + 'recalculating an earlier month has to see the policy that applied then.',
      confirmLabel: 'Stop rule',
      tone: 'danger',
      onConfirm: async () => {
        try {
          const res = await deletePolicyRule(row.id);
          toast.success(res.mode === 'closed'
            ? 'Rule stopped. It stays listed as "ended" so past payroll still resolves it.'
            : 'Rule removed. It had never taken effect, so nothing was kept.');
          await reload();
        } catch (err: any) {
          toast.error(err?.response?.data?.detail || 'Could not remove this rule.');
        }
      },
    });
  };

  // Coverage is the migration-readiness check and is admin-only; a 403 for
  // anyone else is expected, so it fails silently rather than as a page error.
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    getPolicyCoverage()
      .then((c) => { if (!cancelled) setCoverage(c); })
      .catch(() => { /* non-admin or endpoint unavailable — banner just hides */ });
    return () => { cancelled = true; };
  }, [isAdmin]);

  const sections = useMemo(() => {
    if (!policy) return [];
    const q = query.trim().toLowerCase();
    if (!q) return policy.sections;
    return policy.sections
      .map((s) => ({
        ...s,
        rules: s.rules.filter(
          (r) =>
            r.title.toLowerCase().includes(q) ||
            r.question.toLowerCase().includes(q) ||
            r.key.toLowerCase().includes(q),
        ),
      }))
      .filter((s) => s.rules.length > 0);
  }, [policy, query]);

  const activeCount = useMemo(
    () => policy?.sections.flatMap((s) => s.rules).filter((r) => r.is_active).length ?? 0,
    [policy],
  );

  return (
    <div className="min-h-full bg-[#faf8f3]">
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-5">
        <div className="flex items-start gap-3">
          <SlidersHorizontal className="h-6 w-6 text-emerald-700 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-semibold">Policy Rules</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              How this organisation's attendance, late-coming, overtime and leave
              rules are actually applied by payroll.
            </p>
          </div>
        </div>

        {policy && !policy.editable && (
          <div className="flex items-start gap-2 text-sm bg-blue-50 border border-blue-200 text-blue-900 rounded-xl p-3">
            <Info size={16} className="mt-0.5 shrink-0" />
            <p>
              This page is <strong>read-only</strong>. It shows what payroll
              currently applies so you can check it against your written policy.
            </p>
          </div>
        )}

        {policy?.editable && !policy.can_manage && (
          <div className="flex items-start gap-2 text-sm bg-gray-50 border border-gray-200 text-gray-700 rounded-xl p-3">
            <Info size={16} className="mt-0.5 shrink-0" />
            <p>You can view these rules but not change them.</p>
          </div>
        )}

        {coverage && !coverage.legacy_bridge_removable && (
          <div className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-xl p-3">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <p>
              <strong>{coverage.pending.length} branch(es)</strong> haven't been
              migrated yet and still read policy from the old payroll settings.
              Their pay is unaffected, but they won't reflect changes made here.
            </p>
          </div>
        )}

        {loading && <p className="text-sm text-gray-500">Loading policy…</p>}

        {error && (
          <div className="text-sm bg-red-50 border border-red-200 text-red-800 rounded-xl p-3">
            {error}
          </div>
        )}

        {policy && !loading && !error && (
          <>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative flex-1 min-w-[220px]">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search rules…"
                  className="w-full border border-[#e8e3d8] rounded-lg pl-9 pr-3 py-2 text-sm bg-white"
                />
              </div>
              <span className="text-xs text-gray-500">
                {activeCount} rule{activeCount === 1 ? '' : 's'} in effect
                {policy.configured_rule_count > 0 &&
                  ` · ${policy.configured_rule_count} configured for this branch`}
              </span>
            </div>

            {sections.length === 0 ? (
              <p className="text-sm text-gray-500">No rules match “{query}”.</p>
            ) : (
              sections.map((section) => (
                <section key={section.id} className="space-y-2">
                  <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    {section.label}
                  </h2>
                  <div className="space-y-2">
                    {section.rules.map((rule) => (
                      <RuleCard
                        key={rule.key}
                        rule={rule}
                        spec={specs[rule.key]}
                        canEdit={canEdit && Boolean(specs[rule.key])}
                        onEdit={(row) => {
                          setSaveError(null);
                          setEditing({
                            spec: specs[rule.key],
                            row,
                            seed: row ? null : rule.effective_params,
                            exception: false,
                          });
                        }}
                        onDelete={handleDelete}
                      />
                    ))}
                  </div>
                </section>
              ))
            )}
          </>
        )}
      </div>

      {editing && (
        <PolicyRuleEditor
          // Switching to exception mode has to REMOUNT the editor — without a
          // changing key React keeps the instance and its useState seeds stay
          // on the base rule's values.
          key={`${editing.spec.key}:${editing.row?.id ?? 'new'}:${editing.exception ? 'exception' : 'base'}`}
          spec={editing.spec}
          row={editing.row}
          seed={editing.seed}
          exception={editing.exception}
          predicates={predicates}
          saving={saving}
          error={saveError}
          onCancel={() => setEditing(null)}
          onSubmit={handleSubmit}
          // Exceptions are raised from inside the dialog now. The values on
          // screen carry over as the seed, so the exception starts from what
          // the rule currently says instead of a blank form.
          onAddException={(currentParams) => {
            setSaveError(null);
            setEditing({
              spec: editing.spec,
              row: null,
              seed: currentParams,
              exception: true,
            });
          }}
        />
      )}

      {/* In-app confirmation dialog (replaces native window.confirm). */}
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
